/**
 * Typed GL fetch (GL-1 §3 error pattern): every failure resolves to a STATED
 * terminal error, never a silent spinner. Callers render the mapped `glError`.
 *
 * R1 (no server-branch stack): when REACT_APP_GL_MOCK is on, requests are served
 * by the in-memory handlers in gl-mock.js that encode the FROZEN GL-1 §2/§4 DTOs.
 * Otherwise the request goes to the real /gl endpoint via the app's fetch (the
 * store.js interceptor attaches auth). The server enforces its own gates.
 */
import url from "../../constants/urlList";
import { glMockEnabled, glMockHandle } from "./gl-mock";

// HTTP status -> typed terminal error (GL-1 §3 vocabulary + 409 for P13).
export const glErrorForStatus = (status) => {
  if (status === 409) return "conflict";
  if (status === 401 || status === 403) return "disabled";
  if (status === 429) return "cap";
  return "unavailable"; // 404 / 5xx / 0 (network) / anything else
};

export const glFetch = async (path, { method = "GET", body } = {}) => {
  if (glMockEnabled()) {
    const res = glMockHandle(method, path, body);
    if (res) {
      if (res.status >= 200 && res.status < 300) {
        return { ok: true, data: res.data, status: res.status };
      }
      return { ok: false, glError: glErrorForStatus(res.status), status: res.status, data: res.data };
    }
  }
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body !== undefined) {
      opts.body = JSON.stringify(body);
    }
    // The service runs under context-path /qlearn (every urlList entry) — the
    // ROW1 gate's B4/B5a finding was exactly this prefix missing here, so every
    // gl-api call 404'd regardless of flags. Pinned by gl-api.contract.test.js
    // against docs/gl-api-wire-contract.md (QLearn-service repo).
    const resp = await fetch(`${url.domain}/qlearn${path}`, opts);
    const data = await resp.json().catch(() => null);
    if (resp.status >= 200 && resp.status < 300) {
      return { ok: true, data, status: resp.status };
    }
    return { ok: false, glError: glErrorForStatus(resp.status), status: resp.status, data };
  } catch (_) {
    return { ok: false, glError: "unavailable", status: 0 };
  }
};
